import React from 'react';
import { Zap, Mail, Settings } from 'lucide-react';
import { useHistory } from 'react-router-dom';
import { TTemplate, useListTemplatesQuery, useGetProgramById } from '@frontend/app/hooks';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { AutomationBlueprintName } from '@frontend/app/containers/Projects/AutomationConfig/types';
import { WorkletSpecUri, ProjectsRouteRoot } from '@frontend/app/containers/Projects/constants';
import { Badge } from '@/shadcn/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shadcn/components/ui/card';
import { Button } from '@/shadcn/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shadcn/components/ui/dialog';

interface TemplateSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (templateId: string) => void;
  projectId: number;
}

const getTemplateIcon = (templateName: string) => {
  switch (templateName) {
    case AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted:
      return <Mail className="h-6 w-6" />;
    case AutomationBlueprintName.SendEmailAfterApplicationIsAccepted:
      return <Mail className="h-6 w-6 text-blue-500" />;
    case AutomationBlueprintName.BoostAfterApplicationIsAccepted:
      return <Zap className="h-6 w-6 text-yellow-500" />;
    default:
      return <Settings className="h-6 w-6 text-gray-500" />;
  }
};

export const TemplateSelectionModal: React.FC<TemplateSelectionModalProps> = ({
  isOpen,
  onClose,
  onSelectTemplate,
  projectId,
}) => {
  const history = useHistory();
  const { workflowAutomation: isWorkflowAutomationEnabled } = useClientFeatures();

  const {
    data: {
      listTemplates: {
        results: templates,
      },
    } = {
      listTemplates: {
        results: [],
      },
    },
    loading: isLoadingTemplates,
  } = useListTemplatesQuery({
    variables: {
      paging: {
        offset: 0,
        limit: 10,
      },
      context: {
        programId: projectId,
      },
    },
    fetchPolicy: 'network-only',
    skip: !isOpen,
  });

  const program = useGetProgramById({
    variables: {
      id: projectId,
      getWorkItems: true,
    },
    skip: !isOpen,
  });

  const isFlexProject = program.data?.program?.isFlexibleSpec || false;
  const doesProductCatalogStageExists = program.data?.program?.workItems?.some(
    (workItem) => workItem.specURI === WorkletSpecUri.SendBrandProductCatalogWorkletSpecification
  ) || false;

  const checkIfAutomationDisabled = (template: TTemplate) => {
    // Only show PFA order automation for flex projects with product catalog stage
    if (template.blueprint.name === AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted) {
      return !(isFlexProject && doesProductCatalogStageExists);
    }
    return false;
  };

  const handleSelectTemplate = (templateId: string) => {
    // Navigate to V2 template URL - this will show the automation builder in the main content area with sidebar visible
    history.push({
      pathname: `${ProjectsRouteRoot}/${projectId}/automation_v2/template/${templateId}`,
    });

    onSelectTemplate(templateId);
    // Let the parent component handle closing the modal after navigation
  };

  if (isLoadingTemplates) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Select Automation Template</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4" />
              <p className="text-gray-500">Loading templates...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select Automation Template</DialogTitle>
          <p className="text-gray-600 text-sm">
            Choose a pre-made automation template. You can customize the automation in the next step.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6 p-6">
          {templates.map((template: TTemplate) => {
            // Skip templates that require the workflow automation flag
            if (template.blueprint.name === AutomationBlueprintName.SendProductOrderRequestAfterBriefIsAccepted &&
              !isWorkflowAutomationEnabled) {
              return null;
            }

            const isDisabled = checkIfAutomationDisabled(template);
            const isUsed = template.isUsed;
            return (
              <Card
                key={template.blueprint.templateId}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  isDisabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${isUsed ? 'border-green-200 bg-green-50' : 'border-gray-200 hover:border-blue-300'}`}
                onClick={() => {
                  if (!isDisabled && !isUsed) {
                    handleSelectTemplate(template.blueprint.templateId);
                  }
                }}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      {getTemplateIcon(template.blueprint.name)}
                      <div>
                        <CardTitle className="text-base font-medium line-clamp-2">
                          {template.blueprint.name}
                        </CardTitle>
                      </div>
                    </div>
                    {isUsed && (
                      <Badge variant="secondary" className="text-xs">
                        Added
                      </Badge>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardDescription className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {template.blueprint.description}
                  </CardDescription>

                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      variant={isUsed ? 'outline' : 'default'}
                      disabled={isDisabled || isUsed}
                      className={isUsed ? 'cursor-default' : ''}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!isDisabled && !isUsed) {
                          handleSelectTemplate(template.blueprint.templateId);
                        }
                      }}
                    >
                      {isUsed ? 'Added' : 'Continue'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {templates.length === 0 && (
          <div className="text-center py-12">
            <Settings className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No automation templates available for this project.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
